import React, { useState } from 'react';
import { XwebaLogo } from './XwebaLogo';
import { MessageSquare, Mail, MapPin, ArrowUpRight, Sparkles, Phone, Globe, Lock, CheckCircle2, ArrowRight } from 'lucide-react';
import { HostingerPartnerBadge } from './HostingerPartnerBadge';
import { safeFetchJson } from '../utils/apiClient';
import { ServiceKey } from '../data/serviceDetails';

interface FooterProps {
  onOpenBooking: () => void;
  onOpenAudit: () => void;
  onOpenAbout: () => void;
  onNavigateToService?: (serviceKey: ServiceKey) => void;
  onNavigateToServices?: () => void;
  onNavigateToPackages?: () => void;
  onNavigateToFaq?: () => void;
  onNavigateToBlog?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToSitemap?: () => void;
  onOpenContact?: () => void;
  onOpenAdminLogin?: () => void;
  isAdminLoggedIn?: boolean;
}

export const Footer: React.FC<FooterProps> = ({ 
  onOpenBooking, 
  onOpenAudit, 
  onOpenAbout,
  onNavigateToService,
  onNavigateToServices,
  onNavigateToPackages,
  onNavigateToFaq,
  onNavigateToBlog,
  onNavigateToContact,
  onNavigateToSitemap,
  onOpenContact,
  onOpenAdminLogin,
  isAdminLoggedIn = false,
}) => {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);
  const [newsletterError, setNewsletterError] = useState<string | null>(null);

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, callback?: () => void) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (callback) {
      e.preventDefault();
      callback();
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = newsletterEmail.trim();
    if (!trimmedEmail || !trimmedEmail.includes('@') || !trimmedEmail.includes('.')) {
      setNewsletterError('Please enter a valid email address.');
      return;
    }

    setNewsletterError(null);
    setIsSubscribing(true);

    try {
      await Promise.allSettled([
        safeFetchJson('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: trimmedEmail,
            name: trimmedEmail.split('@')[0],
            source: 'Footer Newsletter',
          }),
        }),
        safeFetchJson('/api/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'contact',
            name: 'Newsletter Subscriber',
            email: trimmedEmail,
            topic: 'Newsletter Subscription (AI Web Engineering & SEO/GEO)',
            subject: 'Newsletter Subscription: AI-Ready Web Engineering & SEO/GEO Updates',
            message: `Visitor subscribed to Xweba's newsletter for insights on AI-ready web engineering and SEO/GEO updates (${trimmedEmail}).`,
          }),
        }),
      ]);
    } catch {
      // Fall through to clean confirmation state even if offline
    } finally {
      setIsSubscribing(false);
      setNewsletterSubscribed(true);
      setNewsletterEmail('');
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 pt-16 pb-24 sm:pb-16 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top CTA Banner */}
        <div className="bg-gradient-to-r from-[#0B2D52] via-[#0E3A6B] to-[#F26522]/30 rounded-3xl p-8 sm:p-12 mb-16 border border-slate-700 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-2 text-xs font-mono font-bold text-[#F26522] uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>THE 2026 ADVANTAGE</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
              Ready to turn your website into an always-on growth engine?
            </h3>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              Don’t let your business stay invisible to ChatGPT, Perplexity, and high-intent buyers. Get found, understood, and chosen.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
            <a
              href="/xwebaudit"
              onClick={(e) => handleLinkClick(e, onOpenAudit)}
              className="w-full sm:w-auto px-5 py-3 min-h-[48px] inline-flex items-center justify-center text-xs font-bold text-white bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-600 transition-colors cursor-pointer text-center"
            >
              Free Xwebaudit
            </a>
            <button
              type="button"
              onClick={onOpenBooking}
              className="w-full sm:w-auto px-6 py-3 min-h-[48px] text-xs font-bold text-white bg-[#F26522] hover:bg-[#ea580c] rounded-xl shadow-md transition-all inline-flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Book Growth Session</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Official Hostinger Infrastructure Partnership Banner */}
        <HostingerPartnerBadge variant="footer" className="mb-12" />

        {/* Main Footer Links */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 pb-12 border-b border-slate-800">
          
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-5 space-y-4">
            <XwebaLogo size="lg" variant="light" withTagline={true} />
            <p className="text-xs text-slate-400 max-w-sm leading-relaxed mt-2">
              Xweba is an AI Digital Growth Agency. We build intelligent websites that combine modern design, AI, SEO, GEO, and automated conversion systems.
            </p>
            <div className="text-xs font-semibold text-[#F26522]">
              Get Found. Get Understood. Get Chosen.
            </div>

            {/* Simple, Non-Intrusive Newsletter Subscription Form */}
            <div className="pt-2 max-w-md">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-300 font-semibold mb-1">
                AI Web Engineering & SEO/GEO Dispatch
              </div>
              <p className="text-xs text-slate-400 mb-2.5 leading-relaxed">
                Get Xweba’s actionable insights on AI-ready web architecture, entity schema, and search visibility. No spam.
              </p>
              {newsletterSubscribed ? (
                <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-emerald-950/60 border border-emerald-700/60 text-emerald-300 text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>Subscribed! You’ll receive Xweba’s next AI engineering & GEO briefing.</span>
                </div>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="space-y-1.5" noValidate>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <label htmlFor="footer-newsletter-email" className="sr-only">
                      Email address for Xweba newsletter
                    </label>
                    <input
                      id="footer-newsletter-email"
                      type="email"
                      value={newsletterEmail}
                      onChange={(e) => {
                        setNewsletterEmail(e.target.value);
                        if (newsletterError) setNewsletterError(null);
                      }}
                      placeholder="Enter your email..."
                      required
                      disabled={isSubscribing}
                      className="flex-1 min-h-[44px] px-3.5 py-2 rounded-xl bg-slate-800/90 border border-slate-700 text-xs text-white placeholder-slate-400 focus:outline-none focus:border-[#F26522] focus:ring-1 focus:ring-[#F26522] transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={isSubscribing}
                      className="min-h-[44px] px-4 py-2 rounded-xl bg-[#F26522] hover:bg-[#ea580c] disabled:opacity-60 text-white text-xs font-bold transition-colors inline-flex items-center justify-center gap-1.5 shrink-0 cursor-pointer"
                    >
                      <span>{isSubscribing ? 'Subscribing...' : 'Subscribe'}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {newsletterError && (
                    <p className="text-[11px] text-amber-400 font-medium pl-1">
                      {newsletterError}
                    </p>
                  )}
                </form>
              )}
            </div>

            {/* Official Social Media Profiles */}
            <div className="pt-2">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-400 mb-2.5">
                Connect With Xweba
              </div>
              <div className="flex items-center gap-2.5">
                <a
                  href="https://www.facebook.com/xweba"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Xweba on Facebook"
                  title="Xweba on Facebook"
                  className="w-11 h-11 rounded-xl bg-slate-800/90 hover:bg-[#1877F2] text-slate-300 hover:text-white border border-slate-700 hover:border-[#1877F2] flex items-center justify-center transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
                  </svg>
                </a>

                <a
                  href="https://www.instagram.com/xwebaglobal"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Xweba on Instagram"
                  title="Xweba on Instagram (@xwebaglobal)"
                  className="w-11 h-11 rounded-xl bg-slate-800/90 hover:bg-[#E4405F] text-slate-300 hover:text-white border border-slate-700 hover:border-[#E4405F] flex items-center justify-center transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>

                <a
                  href="https://x.com/xwebaglobal"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Xweba on X"
                  title="Xweba on X (@xwebaglobal)"
                  className="w-11 h-11 rounded-xl bg-slate-800/90 hover:bg-black text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 flex items-center justify-center transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>

                <a
                  href="https://www.linkedin.com/company/xweba/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow Xweba on LinkedIn"
                  title="Xweba on LinkedIn"
                  className="w-11 h-11 rounded-xl bg-slate-800/90 hover:bg-[#0A66C2] text-slate-300 hover:text-white border border-slate-700 hover:border-[#0A66C2] flex items-center justify-center transition-all"
                >
                  <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Independent Pages Directory */}
          <div className="col-span-1 md:col-span-2 space-y-2">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              Navigation
            </h4>
            <ul className="space-y-1 text-xs text-slate-400">
              <li>
                <a
                  href="/services"
                  onClick={(e) => handleLinkClick(e, onNavigateToServices)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-bold text-[#F26522]"
                >
                  Services
                </a>
              </li>
              <li>
                <a
                  href="/packages"
                  onClick={(e) => handleLinkClick(e, onNavigateToPackages)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Packages & WaaS
                </a>
              </li>
              <li>
                <a
                  href="/xwebaudit"
                  onClick={(e) => handleLinkClick(e, onOpenAudit)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center gap-1.5 hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  <Sparkles className="w-3.5 h-3.5 text-[#F26522]" />
                  <span>Free Xwebaudit</span>
                </a>
              </li>
              <li>
                <a
                  href="/faq"
                  onClick={(e) => handleLinkClick(e, onNavigateToFaq)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  FAQ
                </a>
              </li>
              <li>
                <a
                  href="/blog"
                  onClick={(e) => handleLinkClick(e, onNavigateToBlog)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Digital Pulse
                </a>
              </li>
              <li>
                <a
                  href="/contact"
                  onClick={(e) => handleLinkClick(e, onNavigateToContact)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Contact
                </a>
              </li>
              <li>
                <a
                  href="/sitemap"
                  onClick={(e) => handleLinkClick(e, onNavigateToSitemap)}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Website Sitemap
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={onOpenAbout}
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left font-medium text-slate-300 hover:underline cursor-pointer"
                >
                  About Us →
                </button>
              </li>
              {onOpenAdminLogin && (
                <li>
                  <button
                    type="button"
                    onClick={onOpenAdminLogin}
                    className="py-2.5 px-1 min-h-[44px] inline-flex items-center gap-1.5 hover:text-[#F26522] text-slate-400 hover:underline transition-colors text-left font-semibold text-xs cursor-pointer"
                    title="Access Backend CRM & Admin Portal"
                  >
                    <Lock className="w-3.5 h-3.5 text-[#F26522]" />
                    <span>{isAdminLoggedIn ? 'Backend CRM (Active)' : 'Admin Login (CRM)'}</span>
                  </button>
                </li>
              )}
            </ul>
          </div>

          {/* Specialized Capabilities */}
          <div className="col-span-1 md:col-span-2 space-y-2">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              Core Services
            </h4>
            <ul className="space-y-1 text-xs text-slate-400">
              <li>
                <a
                  href="/services/branding-consultant"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('branding') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Branding Consultant (20+ Yrs)
                </a>
              </li>
              <li>
                <a
                  href="/services/social-media-consulting"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('social') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Social Media Consulting
                </a>
              </li>
              <li>
                <a
                  href="/services/digital-smart-advertising"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('ads') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Digital / Smart Advertisement
                </a>
              </li>
              <li>
                <a
                  href="/services/pro-wordpress-ai-websites"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('websites') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer font-semibold text-slate-200"
                >
                  Pro WordPress Websites (AI Cited)
                </a>
              </li>
              <li>
                <a
                  href="/services/geo-optimization"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('search') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer"
                >
                  AI Search (SEO + GEO)
                </a>
              </li>
              <li>
                <a
                  href="/services/automation-pipeline"
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('automation') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer"
                >
                  AI Automation
                </a>
              </li>
              <li>
                <a 
                  href="/services/hostinger-cloud" 
                  onClick={(e) => handleLinkClick(e, () => onNavigateToService ? onNavigateToService('hosting') : onNavigateToServices?.())}
                  className="py-2 px-1 min-h-[40px] inline-flex items-center gap-1 text-purple-300 hover:text-white transition-colors font-semibold cursor-pointer"
                >
                  <span>Hostinger Cloud Engineering</span>
                  <ArrowUpRight className="w-3 h-3 text-[#673DE6]" />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact & Global Hubs */}
          <div className="col-span-2 md:col-span-3 space-y-2">
            <h4 className="text-xs font-bold text-white uppercase tracking-wider font-mono">
              Contact & Inquiries
            </h4>
            <div className="space-y-1 text-xs text-slate-400">
              <div className="flex items-center gap-2 py-2 min-h-[40px]">
                <MapPin className="w-3.5 h-3.5 text-[#F26522] shrink-0" />
                <span>Nairobi, Kenya · Global Remote Delivery</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-3.5 h-3.5 text-[#F26522] shrink-0" />
                {onOpenContact ? (
                  <button
                    type="button"
                    onClick={onOpenContact}
                    className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-left cursor-pointer underline-offset-2 hover:underline"
                    title="Send message to info@xweba.com"
                  >
                    info@xweba.com
                  </button>
                ) : (
                  <a
                    href="mailto:info@xweba.com"
                    className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors"
                  >
                    info@xweba.com
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5 text-[#F26522] shrink-0" />
                <a
                  href="https://wa.me/254725724175"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center gap-1 hover:text-white transition-colors font-semibold text-emerald-400"
                >
                  WhatsApp: +254 725 724 175
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-3.5 h-3.5 text-[#F26522] shrink-0" />
                <a
                  href="tel:+254725724175"
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors"
                >
                  +254 725 724 175
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <Globe className="w-3.5 h-3.5 text-[#F26522] shrink-0" />
                <a
                  href="https://www.xweba.com"
                  className="py-2.5 px-1 min-h-[44px] inline-flex items-center hover:text-white transition-colors text-slate-300 font-medium"
                >
                  www.xweba.com
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar with ample breathing room to prevent overlap with floating chat widget */}
        <div className="pt-8 pb-10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500 border-t border-slate-800/80">
          <div className="text-center md:text-left">
            © 2026 Xweba Digital Growth Partner. All rights reserved.
          </div>
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-x-4 gap-y-2 text-center text-slate-400">
            <span>Generative Engine Optimization (GEO)</span>
            <span aria-hidden="true" className="text-slate-700">·</span>
            <span>Entity Schema Architecture</span>
            <span aria-hidden="true" className="text-slate-700">·</span>
            <a
              href="/sitemap"
              onClick={(e) => handleLinkClick(e, onNavigateToSitemap)}
              className="hover:text-[#F26522] transition-colors font-semibold text-slate-300"
            >
              Sitemap
            </a>
            <span aria-hidden="true" className="text-slate-700">·</span>
            <a
              href="/news-sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#F26522] transition-colors font-semibold text-slate-300"
            >
              Google News Sitemap
            </a>
            <span aria-hidden="true" className="text-slate-700">·</span>
            <a
              href="/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#F26522] transition-colors font-semibold text-slate-300"
            >
              RSS Feed
            </a>
            {onOpenAdminLogin && (
              <>
                <span aria-hidden="true" className="text-slate-700">·</span>
                <button
                  type="button"
                  onClick={onOpenAdminLogin}
                  className="inline-flex items-center gap-1.5 text-slate-400 hover:text-[#F26522] transition-colors font-semibold cursor-pointer py-2 px-2 min-h-[44px]"
                  title="Access Xweba Backend CRM & Admin Portal"
                >
                  <Lock className="w-3.5 h-3.5 text-[#F26522]" />
                  <span>{isAdminLoggedIn ? 'CRM Portal (Active)' : 'Admin Login'}</span>
                </button>
              </>
            )}
          </div>
        </div>

      </div>
    </footer>
  );
};
